<template>
  <div ref="wizardRootRef">
    <Stepper v-model:value="stepperValue" linear class="w-full">
      <StepList>
        <Step value="1">Tracker</Step>
        <Step value="2">General</Step>
        <Step value="3">Stops</Step>
      </StepList>
      <StepPanels>
        <StepPanel value="1" />
        <StepPanel value="2" />
        <StepPanel value="3" />
      </StepPanels>
    </Stepper>

    <ShipmentFormBody
      ref="formBodyRef"
      :visible-section="wizardVisibleSection"
      :general-errors="generalErrors"
      show-planned-range
      :page-context="createPageContext"
      tracker-mode="picker"
      v-model:devices="selectedDevicesPickerValue"
      :device-error="generalErrors.device"
      :load-devices-when-visible="wizardStep === 0"
    />

    <div class="mt-[40px] flex flex-wrap gap-2 sm:justify-end">
      <Button
        v-if="wizardStep > 0"
        type="button"
        label="Back"
        severity="secondary"
        outlined
        size="small"
        class="w-full sm:w-auto"
        tabindex="-1"
        @click="goBack"
      />
      <Button
        type="button"
        label="Cancel"
        severity="secondary"
        outlined
        size="small"
        class="w-full sm:w-auto"
        tabindex="-1"
        @click="onCancel"
      />
      <Button
        v-if="wizardStep < 2"
        type="button"
        :label="stepBusy ? 'Saving' : 'Continue'"
        size="small"
        class="w-full sm:w-auto"
        :disabled="continueDisabled"
        :loading="stepBusy"
        @click="onContinue"
      />
      <Button
        v-else
        type="button"
        :label="stepBusy ? 'Publishing' : 'Publish'"
        size="small"
        class="w-full sm:w-auto"
        :loading="stepBusy"
        @click="onPublish"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import Button from 'primevue/button';
import Step from 'primevue/step';
import StepList from 'primevue/steplist';
import StepPanel from 'primevue/steppanel';
import StepPanels from 'primevue/steppanels';
import Stepper from 'primevue/stepper';
import ShipmentFormBody from '@/components/shipment/form/ShipmentFormBody.vue';
import { useShipmentFormPlannedRangeSync } from '@/composables/useShipmentFormPlannedRangeSync';
import { useShipmentCreateFlow } from '@/composables/useShipmentCreateFlow';
import { useShipmentCreateWizardFocus } from '@/composables/useShipmentCreateWizardFocus';
import { useShipmentFormDeviceSelection } from '@/composables/useShipmentFormDeviceSelection';
import { emptyGeneralErrors } from '@/composables/useShipmentFormValidation';
import { provideShipmentFormStopsSession } from '@/composables/useShipmentFormStopsSession';

useShipmentFormPlannedRangeSync();

const { stopsPort } = provideShipmentFormStopsSession();

const generalErrors = ref(emptyGeneralErrors());
const formBodyRef = ref(null);
const wizardRootRef = ref(null);
const { selectedDevicesPickerValue } = useShipmentFormDeviceSelection();

const {
  wizardStep,
  stepBusy,
  canContinueStepTracker,
  canContinueStepGeneral,
  onContinue,
  onPublish,
  onCancel,
  goBack,
  resetForm,
} = useShipmentCreateFlow({
  stopsPort,
  generalErrors,
});

const { focusActiveSectionWithRetry } = useShipmentCreateWizardFocus({
  wizardStep,
  formBodyRef,
  stepperRootRef: wizardRootRef,
});

const wizardVisibleSection = computed(() => {
  if (wizardStep.value === 0) return 'tracker';
  if (wizardStep.value === 1) return 'general';
  return 'stops';
});

const createPageContext = computed(() => ({
  mode: 'create',
  routesHydrationReady: true,
}));

const continueDisabled = computed(() => {
  if (wizardStep.value === 0) return !canContinueStepTracker.value;
  if (wizardStep.value === 1) return !canContinueStepGeneral.value;
  return false;
});

const stepperValue = computed({
  get() {
    return String((wizardStep.value ?? 0) + 1);
  },
  set(v) {
    const n = Number(String(v));
    if (!Number.isFinite(n) || n < 1 || n > 3) return;
    wizardStep.value = n - 1;
  },
});

onMounted(async () => {
  resetForm();
  await focusActiveSectionWithRetry();
});
</script>

<style scoped>
:deep(mark) {
  padding: 0;
  margin: 0;
  border-width: 0;
}
</style>
